import type { MessageKey } from '@dijitalapp/i18n';
import type { SpaceDto, SpaceType } from '@dijitalapp/shared';
import { useState, type FormEvent } from 'react';
import { api, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useShell } from './Shell';

export function SpacesPage() {
  const { i18n } = useApp();
  const { t, locale } = i18n;
  const { spaces, activeSpace, experience, capabilities, selectSpace, reload } = useShell();
  const [name, setName] = useState('');
  const [type, setType] = useState<SpaceType>('family');
  const [error, setError] = useState<string | null>(null);

  const creatable = capabilities?.createSpaceTypes ?? [];

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const space = await api<SpaceDto>('POST', '/spaces', { type, name });
      setName('');
      await reload();
      selectSpace(space.id);
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  const choosePersona = async (personaKey: string) => {
    if (!activeSpace) return;
    try {
      await api('PUT', `/spaces/${activeSpace.id}/persona`, { personaKey });
      await reload();
    } catch (err) {
      setError(t(errorMessageKey(err)));
    }
  };

  return (
    <div className="content-inner">
      <h1>{t('spaces.title')}</h1>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}

      <section className="card stack" aria-labelledby="spaces-list">
        <h2 id="spaces-list" className="visually-hidden">
          {t('spaces.title')}
        </h2>
        {spaces.map((s) => (
          <button
            key={s.id}
            type="button"
            className="list-item"
            aria-current={s.id === activeSpace?.id}
            onClick={() => selectSpace(s.id)}
          >
            <span className="avatar" aria-hidden>
              {s.name.slice(0, 1).toUpperCase()}
            </span>
            <span style={{ minWidth: 0 }}>
              <strong>{s.name}</strong>
              <br />
              <span className="muted">
                {t(`spaces.type.${s.type}` as MessageKey)} · {t('spaces.members', { count: s.memberCount })}
              </span>
            </span>
            <span className="spacer" />
            <span className="badge">{s.role}</span>
          </button>
        ))}
      </section>

      {activeSpace && experience && (
        <section className="card stack" aria-labelledby="persona-title">
          <h2 id="persona-title">{t('spaces.persona')}</h2>
          <div className="persona-grid">
            {experience.availablePersonas.map((p) => (
              <button
                key={p.key}
                type="button"
                className="persona-option"
                aria-pressed={experience.persona.key === p.key}
                onClick={() => void choosePersona(p.key)}
              >
                <strong>{p.label[locale] ?? p.label.tr}</strong>
                <span className="muted">{p.description[locale] ?? p.description.tr}</span>
              </button>
            ))}
          </div>
          <p className="hint">{t('onboarding.personaNote')}</p>
        </section>
      )}

      {creatable.length > 0 && (
        <form className="card stack" onSubmit={create}>
          <h2>{t('spaces.create')}</h2>
          <div className="field">
            <label htmlFor="space-name">{t('spaces.name')}</label>
            <input id="space-name" className="input" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="space-type">{t('spaces.type')}</label>
            <select id="space-type" className="input" value={type} onChange={(e) => setType(e.target.value as SpaceType)}>
              {creatable.map((st) => (
                <option key={st} value={st}>
                  {t(`spaces.type.${st}` as MessageKey)}
                </option>
              ))}
            </select>
          </div>
          {(capabilities?.verificationRequiredSpaceTypes.length ?? 0) > 0 && (
            <p className="hint">{t('spaces.verificationRequired')}</p>
          )}
          <div className="row">
            <span className="spacer" />
            <button className="btn btn-primary">{t('spaces.create')}</button>
          </div>
        </form>
      )}
    </div>
  );
}
